import React from 'react';
import { Grid, Paper, Typography } from '@mui/material';
import { OverviewDetailInterface } from 'components/Overview/overview.types';
import { StepInterface } from 'pages/Activities/activities.context';
import { formatDatePickerDate, formatMinutes, getUsersName } from 'utils/formatting';
import { themeOptions } from 'theme';
import { Tag } from 'components/Tag/Tag';
import { Div } from 'components/Elements/Div/Div';
import SectionHeader from 'components/SectionHeader/SectionHeader';
import { UsersContextProvider, useUsersContext } from 'context/users.context';
import MediaGallery, { MediaGalleryFileInterface } from 'components/MediaGallery/MediaGallery';
import RowDetails from 'components/Overview/RowDetails/RowDetails';
import OverviewDetail from 'components/Overview/OverviewDetail/OverviewDetail';
import StepQuestionView from 'components/Overview/StepQuestionView';
import { MediaGalleryPageContextProvider } from 'pages/MediaGalleryPage/media-gallery-page.context';

const StepsOverviewComponent = ({
  steps,
  hideMediaComments,
}: {
  steps: StepInterface[];
  hideMediaComments: boolean;
}) => {
  const { getUserById } = useUsersContext();

  if (!steps?.length) {
    return null;
  }
  const stepCount = steps.length;

  const stepRowDetails1 = (step: StepInterface): OverviewDetailInterface[] => [
    {
      title: 'Assigned To',
      value: getUsersName(
        step.assignedUserId ? getUserById(step.assignedUserId) : step.assignedUser
      ),
      width: '7rem',
      marginRight: step.question ? '6.4rem' : '7.2rem',
    },
    ...(step.question
      ? [
          {
            title: 'Start Date',
            value: formatDatePickerDate(step.startDate),
            width: '7rem',
            marginRight: '6.4rem',
          },
          {
            title: 'Due Date',
            value: formatDatePickerDate(step.dueDate),
            width: 'calc(100% - 26.8rem)',
            minWidth: '7rem',
          },
        ]
      : [
          {
            title: 'Description',
            value: step.description,
            richText: true,
            width: 'calc(100% - 14.2rem)',
            minWidth: '17rem',
          },
        ]),
  ];

  const getMediaGalleryData = (step: StepInterface) => {
    const filesToDisplay: MediaGalleryFileInterface[] = [];

    step.media?.forEach((media, index) =>
      filesToDisplay.push({
        id: media.id,
        url: media.signedUrl,
        index,
        fileType: media.fileType,
        mediaComments: media.comments,
      })
    );

    step.files?.forEach((file, index) =>
      filesToDisplay.push({
        url: URL.createObjectURL(file),
        index,
        fileType: file.type,
      })
    );
    return filesToDisplay;
  };

  const activityDetails = (step: StepInterface): OverviewDetailInterface[] => [
    {
      title: 'Media',
      value:
        step.media?.length || step.files?.length ? (
          <MediaGallery
            files={getMediaGalleryData(step)}
            sm={4}
            md={6}
            lg={4}
            xl={3}
            hideComments={hideMediaComments}
          />
        ) : (
          'N/A'
        ),
      xs: 12,
      hide: !step.files?.length && !step.media?.length,
    },
  ];

  const stepRowDetails2 = (step: StepInterface): OverviewDetailInterface[] => {
    if (step.question) {
      return [];
    }

    return [
      {
        title: 'Start Date',
        value: formatDatePickerDate(step.startDate),
        width: '7rem',
        marginRight: '7.2rem',
      },
      {
        title: 'Estimated Time to Complete',
        value: formatMinutes(step.estimatedTimeToComplete),
        width: 'calc(100% - 14.2rem)',
        minWidth: '17rem',
      },
      {
        title: 'Due Date',
        value: formatDatePickerDate(step.dueDate),
        width: '7rem',
        marginRight: '7.2rem',
      },
      {
        title: 'Actual Time to Complete',
        value: formatMinutes(step.actualTimeToComplete),
        width: 'calc(100% - 14.2rem)',
        minWidth: '17rem',
      },
    ];
  };

  return (
    <Grid container>
      {steps.map((step, i) => (
        <Grid
          item
          key={`${step.subject}-${step.id}-${i}`}
          component={Paper}
          mt={i !== 0 ? 2 : 0}
          mr={2}
          padding='1rem'
          width='100%'
          borderRadius='0.75rem'
          elevation={0}
        >
          <Grid container mb={2}>
            <Grid item xs={12}>
              <SectionHeader
                containerPadding='0'
                bold
                alignItems='flex-start'
                title={
                  <>
                    <Typography sx={{ marginBottom: '2rem' }}>
                      Step {i + 1} of {stepCount}
                    </Typography>
                    <Typography variant='h3' component='span'>
                      {step.subject}
                    </Typography>
                  </>
                }
                actions={
                  <Div display='flex' flexWrap='wrap' gap='0.75rem' justifyContent='flex-end'>
                    {step.requiresReview && (
                      <Tag text='Review Required' color={themeOptions.colors.warning} />
                    )}
                    {step.isRequired && (
                      <Tag text='Step Required' color={themeOptions.colors.error900} />
                    )}
                  </Div>
                }
              />
            </Grid>
          </Grid>
          <Grid container mt={3}>
            <Div display='flex' width='100%' flexWrap='wrap'>
              <RowDetails rowDetails={stepRowDetails1(step)} />
            </Div>
            {activityDetails(step as StepInterface).map((detail) => (
              <OverviewDetail
                key={`${(typeof detail.value).toString()}-${detail.title}`}
                {...detail}
              />
            ))}
            {step.question ? (
              <StepQuestionView step={step} />
            ) : (
              <Div display='flex' width='100%' flexWrap='wrap'>
                <RowDetails rowDetails={stepRowDetails2(step)} />
              </Div>
            )}
          </Grid>
        </Grid>
      ))}
    </Grid>
  );
};

const StepsOverview = ({
  steps,
  hideMediaComments,
}: {
  steps: StepInterface[];
  hideMediaComments: boolean;
}) => (
  <MediaGalleryPageContextProvider>
    <UsersContextProvider>
      <StepsOverviewComponent steps={steps} hideMediaComments={hideMediaComments} />
    </UsersContextProvider>
  </MediaGalleryPageContextProvider>
);

export default StepsOverview;
